import * as React from "react";
import { format, parse, parseISO } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { Popover } from "@/components/ui/popover";
import { Input } from "@/components/atoms/Input";
import { Calendar } from "@/components/atoms/Calendar";
import { useEffect } from "react";

interface DatePickerInputProps {
    value?: string; // formato string externo
    onChange: (dateString: string) => void;
    label?: string;
    placeholder?: string;
    inputSize?: "sm" | "md" | "lg";
    disabled?: boolean;
    formatString?: string;
    fromYear?: number;
    toYear?: number;
}

export const DatePickerInput = ({
    value,
    onChange,
    label,
    placeholder = "Selecciona una fecha",
    inputSize = "md",
    disabled = false,
    formatString = "dd/MM/yyyy",
    fromYear,
    toYear,
}: DatePickerInputProps) => {
    const parseDate = (dateStr: string): Date | undefined => {
        if (!dateStr) return undefined;

        // Try to parse using the provided display format first
        const parsedByFormat = parse(dateStr, formatString, new Date());
        if (!Number.isNaN(parsedByFormat.getTime())) return parsedByFormat;

        // Fallback: try ISO-like strings from backend (e.g. "2024-12-31" or with time)
        const parsedIso = parseISO(dateStr);
        if (!Number.isNaN(parsedIso.getTime())) return parsedIso;

        // Last fallback: native Date parsing
        const fallback = new Date(dateStr);
        return Number.isNaN(fallback.getTime()) ? undefined : fallback;
    };

    const initialDate = value ? parseDate(value) : undefined;

    const [date, setDate] = React.useState<Date | undefined>(initialDate);
    const [textValue, setTextValue] = React.useState<string>(() => {
        if (initialDate) return format(initialDate, formatString);
        return "";
    });

    // Sincroniza cambios externos
    useEffect(() => {
        const parsed = value ? parseDate(value) : undefined;
        if (
            parsed &&
            (!date || parsed.getTime() !== date.getTime())
        ) {
            setDate(parsed);
            setTextValue(format(parsed, formatString));
        }
    }, [value]);

    const handleSelect = (selected: Date | undefined) => {
        if (selected) {
            const formatted = format(selected, formatString);
            setDate(selected);
            setTextValue(formatted);
            onChange(formatted);
        }
    };

    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium mb-1">{label}</label>
            )}
            <Popover
                trigger={
                    <div className="relative w-full">
                        <Input
                            readOnly
                            value={textValue}
                            placeholder={placeholder}
                            inputSize={inputSize}
                            disabled={disabled}
                            className="pr-10 cursor-pointer"
                        />
                        <CalendarIcon className="absolute right-2 top-2.5 h-5 w-5 text-gray-500 dark:text-gray-400 pointer-events-none" />
                    </div>
                }
                className="p-0"
            >
                <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleSelect}
                    defaultMonth={date ?? new Date()}
                    captionLayout="dropdown"
                    fromYear={fromYear}
                    toYear={toYear}
                />
            </Popover>
        </div>
    );
};
